import { Divider, Stack } from "@mui/material";
import { CSSProperties, useEffect, useState } from "react";
import { ReactSortable, SortableEvent } from "react-sortablejs";
import { AddedItem, UpdateKeyTitle } from "types/apiTypes";
import { JobTitlesType } from "../../../../../types/enums";
import { KeyTitleAddItemBlock } from "../../../KeyTitles/KeyTitleAddItemBlock";
import { LabelWrapper } from "../AddPreferencesStep/LabelWrapper";
import { TitleItem } from "./TitleItem";

const style: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "8px",
  flexWrap: "wrap",
};

const sortableOptions = {
  animation: 150,
  fallbackOnBody: true,
  swapThreshold: 0.65,
  ghostClass: "ghost",
  forceFallback: true,
  style,
};

const approvedOptions = {
  ...sortableOptions,
  group: { name: "shared", pull: true, put: true },
  filter: ".no-drag, input",
};

const ignoredOptions = {
  ...sortableOptions,
  group: { name: "shared", pull: true, put: true },
  filter: ".no-drag, input",
};

export type ItemType = AddedItem & { is_critical: boolean; id: string };

type Props = {
  approvedList: ItemType[];
  ignoredList: ItemType[];
  changeCriticalHandler: (role: string, title: string) => void;
  removeTitleHandler: (role: string, title: string) => void;
  changeGroupHandler: (role: string, title: string, group: string) => void;
  addTitleHandler: (
    title: string,
    group: string,
    onSuccess: () => void,
  ) => void;
};

export const SortableList = ({
  approvedList,
  ignoredList,
  changeCriticalHandler,
  removeTitleHandler,
  changeGroupHandler,
  addTitleHandler,
}: Props) => {
  const [approved, setApprovedTitles] = useState<ItemType[]>(approvedList);
  const [ignored, setIgnoredTitles] = useState<ItemType[]>(ignoredList);

  useEffect(() => {
    setApprovedTitles(approvedList);
    setIgnoredTitles(ignoredList);
  }, [approvedList, ignoredList]);

  const onAdd = (e: SortableEvent) => {
    const id = e.item.getAttribute("data-id");
    const [group] = e.to.getAttribute("id")?.split(";") || [undefined];

    const [role, title] = id?.split(";") || [undefined, undefined];

    if (role && title && group) changeGroupHandler(role, title, group);
  };

  const changeHandler = (data: Omit<UpdateKeyTitle, "campaign_id">) => {
    const [role, title] = data.id.split(";") || [undefined, undefined];
    if (role && title) changeCriticalHandler(role, title);
  };

  const removeHandler = (id: string) => {
    const [role, title] = id.split(";") || [undefined, undefined];
    if (role && title) removeTitleHandler(role, title);
  };

  return (
    <Stack>
      <LabelWrapper
        gap={"12px"}
        title="Target Job Titles"
        label="Adjust and prioritize the list of key titles that should be approached in the first place."
        isRequired={true}
      >
        <ReactSortable
          list={approved.map((item) => ({ ...item, id: item.id }))}
          setList={setApprovedTitles}
          {...approvedOptions}
          onAdd={onAdd}
          onUpdate={() => {}}
          preventOnFilter={false}
          id={`approved`}
        >
          {approved.map((title) => (
            <TitleItem
              item={{ ...title, id: title.id }}
              key={title.name}
              changeHandler={changeHandler}
              removeHandler={removeHandler}
            />
          ))}

          <KeyTitleAddItemBlock
            titleType={JobTitlesType.approved}
            roleId={"approved"}
            isReachedLimit={true}
            customAddHandler={addTitleHandler}
          />
        </ReactSortable>
      </LabelWrapper>
      <Divider sx={{ width: "100%", marginY: "24px" }} />

      <LabelWrapper
        gap={"12px"}
        title="Excluded titles"
        label="Suppressed titles will be hidden across all roles."
      >
        <ReactSortable
          list={ignored.map((title) => ({ ...title, chosen: true }))}
          setList={setIgnoredTitles}
          {...ignoredOptions}
          onAdd={onAdd}
          onUpdate={() => {}}
          preventOnFilter={false}
          id={`ignored`}
        >
          {ignored.map((title) => (
            <TitleItem
              isIgnored={true}
              item={title}
              key={title.name}
              changeHandler={changeHandler}
              removeHandler={removeHandler}
            />
          ))}

          <KeyTitleAddItemBlock
            titleType={JobTitlesType.ignored}
            roleId={"ignored"}
            isReachedLimit={true}
            customAddHandler={addTitleHandler}
          />
        </ReactSortable>
      </LabelWrapper>
      <Divider
        sx={{
          width: "100%",
          marginTop: ignored.length === 0 ? "12px" : "24px",
        }}
      />
    </Stack>
  );
};
